import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetSubBulkImportParams,
  GetSubBulkImportResponse,
  ImportBulkUpdateParams,
  ImportBulkUpdateResponse,
  BulkUpdateDetailResponse,
  GetEmployeeDataForBulkUpdateResponse,
  GetEmployeeDataForBulkUpdateParams,
  ExportEmployeeDataForBulkUpdateParams,
  ExportEmployeeDataForBulkUpdateResponse,
  GetEmployeeDataByImportIdResponse,
  GetEmployeeDataByImportIdParams,
} from '../type';

const getSubBulkImport = createAsyncThunk<
  GetSubBulkImportResponse,
  GetSubBulkImportParams
>(`bulkUpdate/getSubBulkImport`, async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(`${endPoints.bulkUpdate.import}`, {
      params,
    });
    return {
      data: response.data.data,
      pagination: {
        current: params.page,
        pageSize: params.size,
        totalDocs: response.data.totalDocs,
      },
      queryParams: params,
    };
  } catch (e) {
    return rejectWithValue(e);
  }
});

const importBulkUpdateSub = createAsyncThunk<
  ImportBulkUpdateResponse,
  ImportBulkUpdateParams
>(`bulkUpdate/importBulkUpdate`, async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.post(
      `${endPoints.bulkUpdate.import}`,
      params,
    );
    return response.data.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

const validateImportBulkUpdateSub = createAsyncThunk<
  ImportBulkUpdateResponse,
  { id: string; isBureau?: boolean }
>(
  `bulkUpdate/validateImportBulkUpdate`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.bulkUpdate.validate}/${params.id}`,
        {
          isBureau: params?.isBureau,
        },
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const saveImportBulkUpdateSub = createAsyncThunk<
  ImportBulkUpdateResponse,
  { id: string; isBureau?: boolean }
>(`bulkUpdate/saveImportBulkUpdate`, async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.put(
      `${endPoints.bulkUpdate.save}/${params.id}`,
      {
        isBureau: params?.isBureau,
      },
    );
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

const getBulkUpdateById = createAsyncThunk<
  BulkUpdateDetailResponse,
  { id: string }
>(`bulkUpdate/getBulkUpdateById`, async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(
      `${endPoints.bulkUpdate.import}/${params.id}`,
    );
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

const resendLoginInfo = createAsyncThunk<void, { id: string }>(
  `bulkUpdate/resendLoginInfo`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.bulkUpdate.main}/${params.id}/re-send-mail`,
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getEmployeeDataForBulkUpdate = createAsyncThunk<
  GetEmployeeDataForBulkUpdateResponse,
  GetEmployeeDataForBulkUpdateParams
>(
  `bulkUpdate/getEmployeeDataForBulkUpdate`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.employee.getV2}`, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const exportEmployeeDataForBulkUpdate = createAsyncThunk<
  ExportEmployeeDataForBulkUpdateResponse,
  ExportEmployeeDataForBulkUpdateParams
>(
  `bulkUpdate/exportEmployeeDataForBulkUpdate`,
  async ({ ids, ...params }, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.employee.exportExcel}`,
        { ids },
        {
          params,
        },
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getEmployeeDataByImportId = createAsyncThunk<
  GetEmployeeDataByImportIdResponse,
  GetEmployeeDataByImportIdParams
>(
  `bulkUpdate/getEmployeeDataByImportId`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.bulkUpdate.importById(params.id)}`,
        {
          params,
        },
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const bulkUpdateActs = {
  getSubBulkImport,
  importBulkUpdateSub,
  validateImportBulkUpdateSub,
  saveImportBulkUpdateSub,
  getBulkUpdateById,
  resendLoginInfo,
  getEmployeeDataForBulkUpdate,
  exportEmployeeDataForBulkUpdate,
  getEmployeeDataByImportId,
};
